import { Component, computed, inject, signal } from '@angular/core';
import { formatDate } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatRadioModule } from '@angular/material/radio';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ButtonComponent, ModalLayoutComponent, SnackbarComponent, ToastType, UI_FORM_IMPORTS } from '@program-suite/ui';
import { IconComponent } from '@program-suite/util-icons';
import { InspectionViewModel } from '@program-suite/data-common/program';
import { InspectionsService } from '../data/inspections.service';
import { STATUS_LABELS } from '../data/inspection-status-labels';

enum ManageAction {
  Cancel = 'Cancel',
  Order = 'Order',
}

// An endorsement replaces the inspection rather than just stopping it.
enum CancelReason {
  PolicyCancelled = 'PolicyCancelled',
  Endorsement = 'Endorsement',
}

// Closes with the inspection to show next: the new one after a re-issue or order, the same one after a cancel, undefined if nothing changed.
@Component({
  selector: 'lib-manage-inspection-modal',
  imports: [ModalLayoutComponent, ButtonComponent, IconComponent, MatRadioModule, ReactiveFormsModule, ...UI_FORM_IMPORTS],
  templateUrl: './manage-inspection-modal.component.html',
  styleUrl: './manage-inspection-modal.component.scss',
})
export class ManageInspectionModalComponent {
  inspection = inject(MAT_DIALOG_DATA) as InspectionViewModel;
  dialogRef = inject(MatDialogRef<ManageInspectionModalComponent>);
  inspectionsService = inject(InspectionsService);
  snackbar = inject(MatSnackBar);

  readonly ManageAction = ManageAction;
  readonly CancelReason = CancelReason;
  reasons = [
    { id: CancelReason.PolicyCancelled, name: 'Policy cancelled' },
    { id: CancelReason.Endorsement, name: 'Endorsement issued on the policy' },
  ];
  inspectionNumber = new FormControl(this.inspection.inspectionNumber);
  status = new FormControl(STATUS_LABELS[this.inspection.status!]);
  dateOrdered = new FormControl(formatDate(this.inspection.createDate!, 'MM/dd/yyyy', 'en-US'));
  canCancel = this.inspection.isOpen;
  canOrder = !this.inspection.policyHasOpenInspection;

  action = signal<ManageAction | undefined>(this.canCancel ? ManageAction.Cancel : this.canOrder ? ManageAction.Order : undefined);
  reason = new FormControl<CancelReason | null>(null);
  confirmReissue = new FormControl(false, { nonNullable: true });
  reasonValue = toSignal(this.reason.valueChanges, { initialValue: null });
  private confirmReissueValue = toSignal(this.confirmReissue.valueChanges, { initialValue: false });
  submitting = signal(false);

  submitText = computed(() => {
    if (this.action() === ManageAction.Order) {
      return 'Order Inspection';
    }
    return this.reasonValue() === CancelReason.Endorsement ? 'Cancel Inspection and re-issue' : 'Cancel Inspection';
  });

  submitDisabled = computed(() => {
    if (this.submitting()) {
      return true;
    }
    switch (this.action()) {
      case ManageAction.Order:
        return false;
      case ManageAction.Cancel:
        return this.reasonValue() == null || (this.reasonValue() === CancelReason.Endorsement && !this.confirmReissueValue());
      default:
        return true;
    }
  });

  async submit() {
    const guid = this.inspection.inspectionGuid!;
    this.submitting.set(true);
    try {
      if (this.action() === ManageAction.Order) {
        this.dialogRef.close(await this.inspectionsService.orderInspection(guid));
      } else if (this.reasonValue() === CancelReason.Endorsement) {
        this.dialogRef.close(await this.inspectionsService.reissueInspection(guid));
      } else {
        await this.inspectionsService.cancelInspection(guid);
        this.dialogRef.close(guid);
      }
    } catch (error: any) {
      this.submitting.set(false);
      this.snackbar.openFromComponent(SnackbarComponent, {
        duration: 5000,
        data: { toastType: ToastType.Error, message: error.message },
      });
    }
  }
}
